import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useI18n } from '../i18n/context';
import { getAuditLabel } from '../utils/auditLabels';
import './Dashboard.css';

export default function Dashboard() {
  const { t, formatDateTime } = useI18n();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [recentAudit, setRecentAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api('/superadmin/dashboard')
      .then((data) => setKpis(data.kpis))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

    api('/superadmin/audit?limit=5&page=1')
      .then((data) => setRecentAudit(Array.isArray(data?.data) ? data.data : []))
      .catch(() => setRecentAudit([]))
      .finally(() => setAuditLoading(false));
  }, []);

  if (loading) return <div className="sa-loading">{t('dashboard.loading')}</div>;
  if (error) return <div className="sa-card sa-error">{t('common.error')}: {error}</div>;

  const cards = [
    { label: t('dashboard.activeOrgs'), value: kpis.organizationsActive, sub: t('dashboard.activeOrgsOf', { total: kpis.organizationsTotal }) },
    { label: t('dashboard.activeUsers'), value: kpis.usersActive },
    { label: t('dashboard.entriesToday'), value: kpis.timeEntriesToday }
  ];

  const quickActions = [
    { key: 'create-org', label: t('dashboard.createOrg'), icon: 'orgs', variant: 'primary', to: '/organizations' },
    { key: 'create-user', label: t('dashboard.createUser'), icon: 'users', variant: 'primary', to: '/users' },
    { key: 'view-orgs', label: t('dashboard.viewOrgs'), icon: 'orgs', variant: 'secondary', to: '/organizations' },
    { key: 'view-users', label: t('dashboard.viewUsers'), icon: 'users', variant: 'secondary', to: '/users' },
    { key: 'view-entries', label: t('dashboard.viewEntries'), icon: 'time', variant: 'secondary', to: '/time-entries' },
    { key: 'open-audit', label: t('dashboard.openAudit'), icon: 'audit', variant: 'secondary', to: '/audit' },
    { key: 'security', label: t('dashboard.securitySettings'), icon: 'shield', variant: 'secondary', to: '/security' },
    { key: 'change-password', label: t('dashboard.changePassword'), icon: 'lock', variant: 'secondary', to: '/change-password' }
  ];

  function ActionIcon({ name }) {
    if (name === 'orgs') {
      return (
        <svg viewBox="0 0 24 24" className="sa-action-icon" aria-hidden="true">
          <path d="M3 10h8V4H3v6zm10 10h8V4h-8v16zM3 20h8v-8H3v8z" />
        </svg>
      );
    }
    if (name === 'users') {
      return (
        <svg viewBox="0 0 24 24" className="sa-action-icon" aria-hidden="true">
          <path d="M7.5 12a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm9 3a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7zM3 20a4.5 4.5 0 0 1 9 0v1H3v-1zm11-1a4 4 0 0 1 8 0v1h-8v-1z" />
        </svg>
      );
    }
    if (name === 'time') {
      return (
        <svg viewBox="0 0 24 24" className="sa-action-icon" aria-hidden="true">
          <path d="M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18zm1 4h-2v6l4 2 1-1.7-3-1.3V7z" />
        </svg>
      );
    }
    if (name === 'audit') {
      return (
        <svg viewBox="0 0 24 24" className="sa-action-icon" aria-hidden="true">
          <path d="M4 3h10l6 6v12H4zM13 3v6h6M7 13h10v2H7zm0 4h10v2H7z" />
        </svg>
      );
    }
    if (name === 'shield') {
      return (
        <svg viewBox="0 0 24 24" className="sa-action-icon" aria-hidden="true">
          <path d="M12 3l8 3v6c0 5-3.4 8.7-8 10-4.6-1.3-8-5-8-10V6l8-3z" />
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" className="sa-action-icon" aria-hidden="true">
        <path d="M6 3h12v8H6zM4 13h16v8H4z" />
      </svg>
    );
  }

  return (
    <div className="sa-page-frame">
      <div className="sa-page-header">
        <h1 className="sa-page-title">{t('dashboard.title')}</h1>
        <p className="sa-page-subtitle">{t('dashboard.subtitle')}</p>
      </div>
      <div className="ui-page-content">
        <div className="sa-kpi-grid">
          {cards.map((c) => (
            <div key={c.label} className="sa-kpi-card">
              <span className="sa-kpi-label">{c.label}</span>
              <span className="sa-kpi-value">{c.value}</span>
              {c.sub && <span className="sa-kpi-sub">{c.sub}</span>}
            </div>
          ))}
        </div>

        <div className="sa-dashboard-grid">
          <div className="sa-card sa-dashboard-card">
            <div className="sa-card-header">
              <h2 className="sa-card-title">{t('dashboard.quickActions')}</h2>
              <p className="sa-text-muted">{t('dashboard.quickActionsDesc')}</p>
            </div>
            <div className="sa-actions-grid">
              {quickActions.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className={action.variant === 'primary' ? 'sa-btn-primary' : 'sa-btn-secondary'}
                  onClick={() => navigate(action.to)}
                >
                  <span className="sa-action-btn">
                    <ActionIcon name={action.icon} />
                    <span>{action.label}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="sa-card sa-dashboard-card">
            <div className="sa-card-header">
              <h2 className="sa-card-title">{t('dashboard.recentActivity')}</h2>
              <button type="button" className="sa-btn-link" onClick={() => navigate('/audit')}>
                {t('dashboard.viewAll')}
              </button>
            </div>
            {auditLoading ? (
              <div className="sa-loading">{t('common.loading')}</div>
            ) : recentAudit.length === 0 ? (
              <div className="sa-empty sa-empty-compact">
                <p>{t('dashboard.noRecentActivity')}</p>
                <button type="button" className="sa-btn-secondary" onClick={() => navigate('/audit')}>
                  {t('dashboard.openAudit')}
                </button>
              </div>
            ) : (
              <div className="sa-activity-list">
                {recentAudit.map((item) => (
                  <div key={item.id} className="sa-activity-item">
                    <div className="sa-activity-main">
                      <span className="sa-activity-action">{getAuditLabel(item.action, t)}</span>
                      <span className="sa-activity-meta">
                        {item.actorEmail || item.actorName || item.actorId || '—'}
                      </span>
                    </div>
                    <span className="sa-activity-time">{item.createdAt ? formatDateTime(item.createdAt) : '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="sa-dashboard-grid">
          <div className="sa-card sa-dashboard-card">
            <div className="sa-card-header">
              <h2 className="sa-card-title">{t('dashboard.platformShortcuts')}</h2>
            </div>
            <div className="sa-shortcuts">
              <button type="button" className="sa-btn-link" onClick={() => navigate('/organizations')}>{t('dashboard.manageOrgs')}</button>
              <button type="button" className="sa-btn-link" onClick={() => navigate('/users')}>{t('dashboard.manageUsers')}</button>
              <button type="button" className="sa-btn-link" onClick={() => navigate('/audit')}>{t('dashboard.reviewAudit')}</button>
              <button type="button" className="sa-btn-link" onClick={() => navigate('/security')}>{t('dashboard.securitySettings')}</button>
            </div>
          </div>
          <div className="sa-card sa-dashboard-card sa-status-card">
            <div className="sa-card-header">
              <h2 className="sa-card-title">{t('dashboard.systemStatus')}</h2>
            </div>
            <div className="sa-status-grid">
              <div>
                <span className="sa-status-label">{t('dashboard.apiStatus')}</span>
                <span className="sa-status-value">{t('dashboard.statusOk')}</span>
              </div>
              <div>
                <span className="sa-status-label">{t('dashboard.lastDeployment')}</span>
                <span className="sa-status-value">—</span>
              </div>
              <div>
                <span className="sa-status-label">{t('dashboard.twoFaAvailable')}</span>
                <span className="sa-status-value">{t('dashboard.statusOk')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
